/**
 * "¿Cuánto le quedo debiendo, y ya le pagué?"
 *
 * Va en Revisar, debajo de los totales. Dos controles y un aviso:
 *
 *   · EL IMPORTE, editable. Arranca en el total del papel porque es lo que se le
 *     debe, no la suma de lo que entró al stock. Si no coinciden avisa, y no
 *     bloquea: un flete que no se carga o un ítem que no se cataloga son razones
 *     legítimas, y frenar la carga por eso obligaría a mentirle al sistema.
 *     Lleva la moneda de la compra al lado ($ / US$): sin ella, la factura en
 *     pesos se tipeaba en una compra en dólares y quedaba una deuda de
 *     US$ 1.163.684 con un aviso azul que decía «Es normal». Cuando el importe
 *     parece de la otra moneda o se pasa más de un 20% de las líneas, el aviso
 *     es amarillo y hay que tildar que está bien para poder aplicar.
 *
 *   · EL SWITCH «Ya está paga», que despliega el mismo editor de medios que usan
 *     el mostrador y la cobranza. Sin marcarlo, la deuda queda abierta y se paga
 *     después desde la ficha del proveedor.
 *
 * SIN PROVEEDOR EL BLOQUE NO DESAPARECE, LO DICE
 * ----------------------------------------------
 * Una compra sin proveedor no puede generar deuda. Esconder el bloque haría que
 * eso pase inadvertido; mostrarlo con el motivo convierte "esta compra no va a
 * la cuenta de nadie" en una decisión visible.
 */
import { useEffect } from "react";

import { type Moneda, formatearNumero } from "@tora/shared";

import { Aviso } from "@/components/ui/aviso";
import { Campo } from "@/components/catalogo/Campos";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { ChipMoneda, Importe } from "@/components/money";
import { EditorPagos, nuevoPago } from "@/components/cobro/EditorPagos";
import type { CuentaFinanciera, PagoCobro } from "@/components/cobro/logica";
import { AvisoCajasEnNegativo } from "@/components/proveedores/AvisoCajasEnNegativo";
import { aNumero, type Caja } from "@/lib/tipos";

import {
  faltaPorCubrir,
  deudaPropuesta,
  importeEscrito,
  sospechaDeDeuda,
  type Compra,
  type FilaCompra,
} from "./logica";

const TITULO_SOSPECHA = {
  pesos: "¿Es en pesos? La compra es en dólares",
  dolares: "¿Es en dólares? La compra es en pesos",
  grande: "Es bastante más que lo que entra",
} as const;

export interface BloqueDeudaProps {
  compra: Compra;
  filas: ReadonlyArray<FilaCompra>;
  cuentas: CuentaFinanciera[];
  cotizacion: number;
  /** Lo que hay en el campo del importe, como texto. Vacío = usar el propuesto. */
  totalTexto: string;
  onTotalTexto: (v: string) => void;
  /** Se llama al salir del campo: es cuando se persiste `total_a_pagar`. */
  onTotalConfirmado: (v: number | null) => void;
  yaPaga: boolean;
  onYaPaga: (v: boolean) => void;
  pagos: PagoCobro[];
  onPagos: (p: PagoCobro[]) => void;
  /** Los saldos de las cajas, para avisar si el pago deja una en negativo. */
  cajas?: ReadonlyArray<Caja>;
  /**
   * El total declarado no cierra con las líneas y la revisión ya frena por
   * eso, con su salida. El aviso «Es normal si hay flete» decía lo contrario
   * en la misma pantalla, así que en ese estado no se muestra.
   */
  totalNoCierra?: boolean;
  /** Se tildó que el importe sospechoso está bien (ver `sospechaDeDeuda`). */
  confirmada?: boolean;
  onConfirmada?: (v: boolean) => void;
}

export function BloqueDeuda({
  compra,
  filas,
  cuentas,
  cotizacion,
  totalTexto,
  onTotalTexto,
  onTotalConfirmado,
  yaPaga,
  onYaPaga,
  pagos,
  onPagos,
  cajas = [],
  totalNoCierra = false,
  confirmada = false,
  onConfirmada,
}: BloqueDeudaProps): JSX.Element {
  const propuesta = deudaPropuesta(compra, filas);
  const propuestaSinAMano = deudaPropuesta({ ...compra, total_a_pagar: null }, filas);
  const sinProveedor = compra.proveedor_id === null;
  const moneda: Moneda = propuesta.moneda ?? "USD";

  // `parsearImporte` y no `Number`: en Argentina el punto es el separador de
  // miles, y `Number("1.590")` da 1,59. Escribir la deuda como "1.234.567" la
  // convertia en $ 1,23. Ver `money.ts:445`.
  const escrito = importeEscrito(totalTexto);
  const escritoValido = escrito !== null;
  const importe = escrito ?? propuesta.importe;
  const coincide = Math.abs(importe - propuesta.suma) < 0.01;
  const sospecha = totalNoCierra
    ? null
    : sospechaDeDeuda(importe, propuesta.suma, propuesta.moneda, cotizacion, propuesta.papel);

  // Al prender "ya está paga" se propone una fila con el total de la deuda.
  useEffect(() => {
    if (!yaPaga || pagos.length > 0) return;
    const inicial = nuevoPago(cuentas, [], importe, moneda, cotizacion);
    if (inicial) onPagos([inicial]);
    // `pagos` fuera de las dependencias a propósito: esto corre al prender el
    // switch, no cada vez que se edita un importe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yaPaga, cuentas, importe, moneda, cotizacion]);

  const falta = faltaPorCubrir(importe, pagos, cuentas, moneda, cotizacion);

  if (sinProveedor) {
    return (
      <Card padding="normal" className="space-y-2">
        <TituloSeccion>Deuda con el proveedor</TituloSeccion>
        <Aviso tono="warning">
          Esta compra no tiene proveedor, así que no va a quedar anotada en ninguna cuenta. Elegilo
          arriba si querés que la deuda se registre.
        </Aviso>
      </Card>
    );
  }

  return (
    <Card padding="normal" className="space-y-4">
      <TituloSeccion>Deuda con el proveedor</TituloSeccion>

      <Campo
        label="Cuánto le debo"
        id="deuda-total"
        ayuda={
          compra.total_a_pagar === null && compra.total_declarado === null
            ? "Si no cargaste el total de la factura, es lo que llegó. Cambialo si la factura dice otra cosa: un flete, un ítem que no va al stock."
            : "Lo que dice la factura. Puede no coincidir con las líneas: un flete, un ítem que no va al stock."
        }
      >
        {/* Con la moneda al lado. El campo sigue siendo texto y no InputImporte
            porque vacío quiere decir «el propuesto», no cero. */}
        <div className="flex items-center gap-2">
          <span
            className="text-sm font-medium tabular-nums text-muted-foreground"
            aria-hidden="true"
          >
            {moneda === "ARS" ? "$" : "US$"}
          </span>
          <Input
            id="deuda-total"
            type="text"
            inputMode="decimal"
            value={totalTexto}
            aria-label={`Cuánto le debo, en ${moneda === "ARS" ? "pesos" : "dólares"}`}
            // Como se escribe aca: «54,60», no «54.6», que en este campo se lee
            // como 546 (el punto es separador de miles).
            // El ejemplo es lo que vale con el campo vacío: el total de la
            // factura o la suma de las líneas, no lo guardado a mano, que ya
            // está escrito en el campo.
            placeholder={formatearNumero(propuestaSinAMano.importe, 2)}
            onChange={(e) => onTotalTexto(e.target.value)}
            onBlur={() => {
              // Solo si cambió: entrar y salir sin tocar nada no escribe.
              const nuevo = escritoValido ? escrito : null;
              const guardado = compra.total_a_pagar === null ? null : aNumero(compra.total_a_pagar);
              if (nuevo !== guardado) onTotalConfirmado(nuevo);
            }}
            className="w-40 tabular-nums"
          />
          <ChipMoneda moneda={moneda} />
        </div>
      </Campo>

      {coincide || totalNoCierra ? null : sospecha !== null ? (
        <Aviso tono="warning" titulo={TITULO_SOSPECHA[sospecha]}>
          <div className="space-y-2">
            <p>
              La suma de las líneas da{" "}
              <Importe monto={propuesta.suma} moneda={moneda} tamano="sm" codigo={false} /> y acá
              dice <Importe monto={importe} moneda={moneda} tamano="sm" codigo={false} />
              {sospecha === "grande"
                ? ". Un flete no suele pesar tanto: revisá que sea lo que dice la factura."
                : `: ${sospecha === "pesos" ? "en dólares" : "en pesos"} sería más o menos ${formatearNumero(sospecha === "pesos" ? importe / cotizacion : importe * cotizacion, 2)}. Escribilo en la moneda de la compra.`}
            </p>
            <Checkbox
              label="Está bien, es lo que le debo"
              checked={confirmada}
              onChange={(e) => onConfirmada?.(e.target.checked)}
            />
          </div>
        </Aviso>
      ) : (
        <Aviso tono="info" compacto>
          La suma de las líneas da{" "}
          <Importe monto={propuesta.suma} moneda={moneda} tamano="sm" codigo={false} /> y la factura{" "}
          <Importe monto={importe} moneda={moneda} tamano="sm" codigo={false} />. Es normal si hay
          flete o algo que no entra al stock.
        </Aviso>
      )}

      <Checkbox
        label="Ya está paga"
        checked={yaPaga}
        onChange={(e) => {
          onYaPaga(e.target.checked);
          if (!e.target.checked) onPagos([]);
        }}
      />

      {yaPaga ? (
        <div className="space-y-2">
          <EditorPagos
            pagos={pagos}
            onChange={onPagos}
            cuentas={cuentas}
            objetivo={importe}
            monedaObjetivo={moneda}
            cotizacion={cotizacion}
            etiquetaVacio="Agregar un medio de pago"
          />
          <AvisoCajasEnNegativo pagos={pagos} cajas={cajas} />
          {Math.abs(falta) > 0.009 ? (
            <Aviso tono={falta > 0 ? "warning" : "info"} compacto>
              {falta > 0 ? (
                <>
                  Falta cubrir <Importe monto={falta} moneda={moneda} tamano="sm" codigo={false} />.
                  El resto queda como deuda abierta.
                </>
              ) : (
                <>
                  Estás pagando{" "}
                  <Importe monto={-falta} moneda={moneda} tamano="sm" codigo={false} /> de más. La
                  diferencia queda a tu favor en la cuenta.
                </>
              )}
            </Aviso>
          ) : null}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          Sin marcar, la deuda queda abierta y se paga después desde la ficha del proveedor.
        </p>
      )}
    </Card>
  );
}
